import { useEffect, useRef, useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'

// WebSocket protocol constants (must match ws/protocol.go)
const PROTO_VERSION = 1
const MSG_DATA      = 0x01
const MSG_RESIZE    = 0x02
const MSG_CLIPBOARD = 0x03
const MSG_CRED_PROMPT = 0x04
const MSG_CRED_REPLY  = 0x05
const MSG_CONTROL   = 0x06

function encodeFrame(type: number, payload: Uint8Array): Uint8Array {
  const buf = new Uint8Array(2 + payload.byteLength)
  buf[0] = PROTO_VERSION
  buf[1] = type
  buf.set(payload, 2)
  // Prepend 4-byte length
  const frame = new Uint8Array(4 + buf.byteLength)
  const view = new DataView(frame.buffer)
  view.setUint32(0, buf.byteLength)
  frame.set(buf, 4)
  return frame
}

function encodeJSON(type: number, obj: object): Uint8Array {
  const enc = new TextEncoder()
  return encodeFrame(type, enc.encode(JSON.stringify(obj)))
}

interface CredentialPrompt {
  prompt_id: string
  prompts: Array<{ label: string; echo: boolean }>
}

export default function SSHTerminal() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const termRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'denied'>('connecting')
  const [credPrompt, setCredPrompt] = useState<CredentialPrompt | null>(null)
  const [credInputs, setCredInputs] = useState<string[]>([])
  const [clipboardAllowed, setClipboardAllowed] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const terminalRef = useRef<Terminal | null>(null)
  const fitRef = useRef<FitAddon | null>(null)

  const sendResize = useCallback((cols: number, rows: number) => {
    wsRef.current?.send(encodeJSON(MSG_RESIZE, { cols, rows }))
  }, [])

  useEffect(() => {
    if (!termRef.current || !resourceId) return

    // Initialize xterm.js
    const term = new Terminal({
      theme: { background: '#0a0a0a', foreground: '#e5e7eb', cursor: '#6366f1' },
      fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
      fontSize: 14,
      cursorBlink: true,
    })
    const fitAddon = new FitAddon()
    term.loadAddon(fitAddon)
    term.open(termRef.current)
    fitAddon.fit()
    terminalRef.current = term
    fitRef.current = fitAddon

    // Keyboard input → WebSocket
    term.onData((data) => {
      const enc = new TextEncoder()
      wsRef.current?.send(encodeFrame(MSG_DATA, enc.encode(data)))
    })

    // Handle terminal resize
    term.onResize(({ cols, rows }) => {
      sendResize(cols, rows)
    })

    // Open WebSocket
    const ws = new WebSocket(
      `wss://${window.location.host}/session/ssh?resource_id=${resourceId}`,
    )
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      sendResize(term.cols, term.rows)
    }

    ws.onmessage = (event) => {
      const data = new Uint8Array(event.data as ArrayBuffer)
      // Skip 4-byte length prefix
      if (data.byteLength < 6) return
      const msgStart = 4
      const version = data[msgStart]
      const type = data[msgStart + 1]
      const payload = data.slice(msgStart + 2)
      const dec = new TextDecoder()

      if (version !== PROTO_VERSION) return

      switch (type) {
        case MSG_DATA:
          setStatus('connected')
          term.write(payload)
          break
        case MSG_CONTROL: {
          try {
            const ctrl = JSON.parse(dec.decode(payload))
            if (ctrl.event === 'session_terminated') {
              setStatus('disconnected')
              term.writeln('\r\n\x1b[33m[Session ended: ' + ctrl.message + ']\x1b[0m')
            } else if (ctrl.event === 'error') {
              setStatus('disconnected')
              term.writeln('\r\n\x1b[31m[Error: ' + ctrl.message + ']\x1b[0m')
            } else if (ctrl.event === 'clipboard_denied') {
              term.writeln('\r\n\x1b[31m[Clipboard access denied by policy]\x1b[0m')
              setClipboardAllowed(false)
            } else if (ctrl.event === 'clipboard_allowed') {
              setClipboardAllowed(true)
            }
          } catch {}
          break
        }
        case MSG_CRED_PROMPT: {
          try {
            const prompt: CredentialPrompt = JSON.parse(dec.decode(payload))
            setStatus('connecting')
            setCredPrompt(prompt)
            setCredInputs(new Array(prompt.prompts.length).fill(''))
          } catch {}
          break
        }
        case MSG_CLIPBOARD: {
          try {
            const clip = JSON.parse(dec.decode(payload))
            if (clip.direction === 'to_browser' && clipboardAllowed) {
              // Async Clipboard API: requires secure context + user gesture/focus.
              // Remote→local is explicit user action, not a silent timer.
              navigator.clipboard?.writeText(clip.data).catch(() => {
                // Clipboard API not available or permission denied; show in terminal.
                term.writeln('\r\n[Clipboard: ' + clip.data + ']')
              })
            }
          } catch {}
          break
        }
      }
    }

    ws.onclose = () => {
      setStatus('disconnected')
    }

    ws.onerror = () => {
      setStatus('disconnected')
    }

    // Resize observer
    const observer = new ResizeObserver(() => {
      fitAddon.fit()
    })
    observer.observe(termRef.current)

    // Clipboard paste support (requires secure context + user gesture).
    const handlePaste = async () => {
      if (!clipboardAllowed) return
      try {
        const text = await navigator.clipboard.readText()
        if (text) {
          ws.send(encodeJSON(MSG_CLIPBOARD, {
            direction: 'to_server',
            mime_type: 'text/plain',
            data: text,
          }))
        }
      } catch {}
    }
    termRef.current.addEventListener('paste', handlePaste)

    return () => {
      observer.disconnect()
      ws.close()
      term.dispose()
    }
  }, [resourceId, sendResize, clipboardAllowed])

  const handleDisconnect = useCallback(() => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close(1000, 'user disconnect')
    }
    wsRef.current = null
    setStatus('disconnected')
    terminalRef.current?.writeln('\r\n\x1b[33m[Session closed]\x1b[0m')
  }, [])

  // Submit credential reply
  const handleCredSubmit = useCallback(() => {
    if (!credPrompt || !wsRef.current) return
    wsRef.current.send(encodeJSON(MSG_CRED_REPLY, {
      prompt_id: credPrompt.prompt_id,
      responses: credInputs,
    }))
    // Zero inputs immediately after sending — credentials should not linger.
    setCredInputs((prev) => prev.map(() => ''))
    setCredPrompt(null)
  }, [credPrompt, credInputs])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Status bar — outside terminal mount */}
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2 text-sm">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            status === 'connected' ? 'bg-success animate-pulse'
            : status === 'connecting' ? 'bg-warning animate-pulse'
            : status === 'denied' ? 'bg-danger'
            : 'bg-muted'
          }`}
        />
        <span className="capitalize text-foreground">{status}</span>
        <span className="text-muted">SSH — {resourceId}</span>
        <div className="ml-auto flex items-center gap-2">
          {clipboardAllowed && (
            <span className="rounded-md border border-success/20 bg-success-muted px-2 py-0.5 text-xs text-success-foreground">
              clipboard on
            </span>
          )}
          {(status === 'connected' || status === 'connecting') && (
            <button
              type="button"
              onClick={handleDisconnect}
              className="rounded-lg border border-danger/40 bg-danger-muted px-3 py-1 text-xs font-medium text-danger-foreground transition-colors hover:bg-danger/20"
            >
              Disconnect
            </button>
          )}
          {status === 'disconnected' && (
            <Link
              to="/catalog"
              className="rounded-lg border border-border px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-surface-muted"
            >
              Back to Resources
            </Link>
          )}
        </div>
      </div>

      {/* Terminal mount — do not change box model */}
      <div ref={termRef} className="terminal-container flex-1" />

      {/* Credential prompt — outside mount */}
      {credPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-sm">
            <h3 className="mb-4 font-semibold text-foreground">Authentication Required</h3>
            <div className="space-y-3">
              {credPrompt.prompts.map((prompt, i) => (
                <div key={i}>
                  <label className="mb-1 block text-sm text-muted">{prompt.label}</label>
                  <input
                    type={prompt.echo ? 'text' : 'password'}
                    autoFocus={i === 0}
                    value={credInputs[i] || ''}
                    onChange={(e) => {
                      const next = [...credInputs]
                      next[i] = e.target.value
                      setCredInputs(next)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCredSubmit()
                    }}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={handleCredSubmit}
              className="mt-4 w-full rounded-lg bg-primary py-2 font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
